import React from 'react';
import LeadPanel from './LeadPanel';
import RegionalSuggestionModal from './RegionalSuggestionModal';
import ObjectionPanel from './ObjectionPanel';
import storage from '../services/storage';

/* ── Persistence ────────────────────────────────────────────────────────────
   Gelöschte Sessions werden als Tombstones ({ id, deleted, updatedAt }) im
   Array behalten, damit Löschungen zwischen Geräten propagieren (Merge pro
   Session-ID in userDataSync.mergeSessionArrays). */
const SESSIONS_KEY = 'crm_call_sessions';

function readRawSessions() {
  const raw = storage.get(SESSIONS_KEY, []);
  return Array.isArray(raw) ? raw : [];
}

function writeRawSessions(arr) {
  const live  = arr.filter(s => s && !s.deleted).slice(0, 20);
  const tombs = arr.filter(s => s && s.deleted);
  storage.set(SESSIONS_KEY, [...live, ...tombs]);
  window.dispatchEvent(new CustomEvent('crm-sessions-updated'));
}

export function loadSavedSessions() {
  return readRawSessions().filter(s => !s.deleted);
}

/** Eine Session anlegen/aktualisieren, Rest unangetastet lassen. */
export function saveSession(session) {
  const rest = readRawSessions().filter(s => s.id !== session.id);
  writeRawSessions([{ ...session, updatedAt: session.updatedAt || new Date().toISOString() }, ...rest]);
}

/** Session löschen — hinterlässt einen Tombstone für den Geräte-Sync. */
export function deleteSessionById(id) {
  const rest = readRawSessions().filter(s => s.id !== id);
  writeRawSessions([{ id, deleted: true, updatedAt: new Date().toISOString() }, ...rest]);
}
function fmtSessionDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
           ' · ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr';
  } catch { return iso; }
}

/* ── Call Session Overlay ───────────────────────────────────────────────── */
export function CallSessionOverlay({ leads, sessionId: initId, startIndex = 0, initDone = [], onClose, onSessionComplete, onUpdateLead, onSaveTask, statusMeta, lists, bestandskunden }) {
  const sessionId = React.useRef(initId || Date.now());
  const [index, setIndex] = React.useState(startIndex);
  const [done,  setDone]  = React.useState(new Set(initDone));
  const [regionalLead, setRegionalLead] = React.useState(null);
  const [showObjection, setShowObjection] = React.useState(false);

  const current = leads[index];
  const isLast  = index >= leads.length - 1;

  React.useEffect(() => {
    const existing = loadSavedSessions().find(s => s.id === sessionId.current);
    saveSession({
      id: sessionId.current,
      name: existing?.name || ('Call Session – ' + new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })),
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      leadIds: leads.map(l => l.id),
      leadsSnapshot: leads,
      index,
      done: [...done],
      total: leads.length,
    });
  }, [index, done]); // eslint-disable-line

  function finishSession() {
    deleteSessionById(sessionId.current);
    onSessionComplete?.({ excludeLeadIds: leads.map(l => l.id), source: 'call_session' });
    onClose();
  }

  function goNext() {
    if (!isLast) setIndex(i => i + 1);
    else finishSession();
  }

  function handleActivitySaved(callData) {
    if (callData?.outcome === 'reached_appt') setRegionalLead({ ...current });
    else goNext();
  }

  function markDone() {
    setDone(prev => new Set([...prev, current?.id]));
    goNext();
  }

  if (!current) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 450, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>

      {/* ── Session bar ── */}
      <div style={{ background: 'var(--bg2)', borderBottom: '2px solid var(--border)', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, boxShadow: '0 2px 8px rgba(15,23,42,0.06)' }}>

        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', minWidth: 90 }}>
          Lead {index + 1}/{leads.length}
        </span>

        <button onClick={() => setIndex(i => i - 1)} disabled={index === 0}
          style={{ width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', cursor: index === 0 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: index === 0 ? 0.35 : 1, transition: 'opacity .12s' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button onClick={() => setIndex(i => Math.min(i + 1, leads.length - 1))} disabled={isLast}
          style={{ width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', cursor: isLast ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isLast ? 0.35 : 1, transition: 'opacity .12s' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </button>

        <div style={{ display: 'flex', gap: 5, flex: 1, overflowX: 'auto', scrollbarWidth: 'none', padding: '0 6px', alignItems: 'center' }}>
          {leads.map((l, i) => {
            const isDone = done.has(l.id);
            const isCur  = i === index;
            return (
              <button key={l.id} onClick={() => setIndex(i)} title={l.name}
                style={{ height: 8, width: isCur ? 28 : 8, minWidth: isCur ? 28 : 8, borderRadius: 4, border: 'none', cursor: 'pointer', flexShrink: 0, transition: 'all .25s ease',
                  background: isDone ? '#16a34a' : isCur ? 'var(--accent)' : 'var(--bg4)',
                }} />
            );
          })}
        </div>

        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {current.name} · {current.company}
        </span>

        <div style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />

        <button onClick={goNext}
          style={{ height: 34, padding: '0 14px', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontFamily: 'inherit', transition: 'all .12s' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg4)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text2)'; }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="5 4 15 12 5 20"/><line x1="19" y1="5" x2="19" y2="19"/></svg>
          Überspringen
        </button>

        <button onClick={markDone}
          style={{ height: 34, padding: '0 18px', borderRadius: 8, border: 'none', background: done.has(current.id) ? 'linear-gradient(135deg,#15803d,#16a34a)' : 'linear-gradient(135deg,#ea580c,#f97316)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(249,115,22,0.3)', transition: 'background .2s' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
          {isLast ? 'Session beenden' : 'Abgeschlossen'}
        </button>

        <div style={{ width: 1, height: 22, background: 'var(--border)', flexShrink: 0 }} />

        <button onClick={() => setShowObjection(v => !v)} title="Einwandsbehandlung ein-/ausblenden"
          style={{ height: 34, padding: '0 12px', borderRadius: 8, border: `1.5px solid ${showObjection ? 'rgba(15,138,83,0.4)' : 'var(--border)'}`, background: showObjection ? 'rgba(15,138,83,0.1)' : 'var(--bg3)', color: showObjection ? '#0f8a53' : 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, gap: 5, transition: 'all .15s', fontSize: 12, fontWeight: showObjection ? 600 : 400, whiteSpace: 'nowrap' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Einwände
        </button>

        <button onClick={onClose} title="Session pausieren & speichern"
          style={{ width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .12s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        <LeadPanel
          key={current.id}
          lead={current}
          onClose={onClose}
          onUpdateLead={onUpdateLead}
          onSaveTask={onSaveTask}
          statusMeta={statusMeta}
          lists={lists}
          bestandskunden={bestandskunden}
          onActivitySaved={handleActivitySaved}
        />
      </div>

      {showObjection && (
        <ObjectionPanel
          branch={current?.branch}
          lead={current}
          initialX={Math.max(20, window.innerWidth - 720)}
          initialY={70}
          zIndex={460}
        />
      )}

      {regionalLead && (
        <RegionalSuggestionModal
          sourceLead={regionalLead}
          onClose={() => { setRegionalLead(null); goNext(); }}
        />
      )}
    </div>
  );
}

/* ── Saved Sessions Modal ───────────────────────────────────────────────── */
export function CallSessionsModal({ allLeads, onClose, onResume, onNew }) {
  const [sessions,    setSessions]    = React.useState(loadSavedSessions);
  // Live-Refresh, wenn Sessions von einem anderen Gerät gesynct werden
  React.useEffect(() => {
    const update = () => setSessions(loadSavedSessions());
    window.addEventListener('crm-sessions-updated', update);
    return () => window.removeEventListener('crm-sessions-updated', update);
  }, []);
  const [editingId,   setEditingId]   = React.useState(null);
  const [editingName, setEditingName] = React.useState('');
  const [expandAdd,   setExpandAdd]   = React.useState(null);
  const [addSearch,   setAddSearch]   = React.useState('');
  const [addSelected, setAddSelected] = React.useState(new Set());

  function deleteSession(id) {
    deleteSessionById(id);
    setSessions(loadSavedSessions());
  }

  function saveName(id) {
    const trimmed = editingName.trim();
    if (!trimmed) { setEditingId(null); return; }
    const session = sessions.find(s => s.id === id);
    if (session) saveSession({ ...session, name: trimmed, updatedAt: new Date().toISOString() });
    setSessions(loadSavedSessions());
    setEditingId(null);
  }

  function openAddLeads(sessionId) {
    setExpandAdd(expandAdd === sessionId ? null : sessionId);
    setAddSearch('');
    setAddSelected(new Set());
  }

  function confirmAddLeads(session) {
    if (addSelected.size === 0) { setExpandAdd(null); return; }
    const newLeads = allLeads.filter(l => addSelected.has(l.id));
    const existingIds = new Set((session.leadsSnapshot || []).map(l => l.id));
    const toAdd = newLeads.filter(l => !existingIds.has(l.id));
    const updatedSnapshot = [...(session.leadsSnapshot || []), ...toAdd];
    saveSession({
      ...session,
      leadsSnapshot: updatedSnapshot,
      leadIds: updatedSnapshot.map(l => l.id),
      total: updatedSnapshot.length,
      updatedAt: new Date().toISOString(),
    });
    setSessions(loadSavedSessions());
    setExpandAdd(null);
    setAddSelected(new Set());
  }

  const existingIdsForSession = (session) => new Set((session.leadsSnapshot || []).map(l => l.id));

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px' }}>
      <div style={{ background: 'var(--bg2)', borderRadius: 18, width: 'min(560px, 100%)', maxHeight: '90vh', boxShadow: '0 24px 60px rgba(15,23,42,0.24)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>Call Sessions</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Pausierte Sessions fortsetzen, umbenennen oder erweitern</div>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--bg4)', border: 'none', cursor: 'pointer', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {sessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ display: 'block', margin: '0 auto 12px', opacity: 0.35 }}>
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.08 12.08 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.08 12.08 0 002.81.7A2 2 0 0122 16.92z"/>
              </svg>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)' }}>Keine gespeicherten Sessions</div>
              <div style={{ fontSize: 13, marginTop: 4, marginBottom: 20 }}>Starte jetzt eine neue Call Session mit deinen Kontakten.</div>
              <button onClick={onNew} style={{ height: 44, padding: '0 24px', borderRadius: 12, background: 'linear-gradient(135deg,#0d9488,#159c5f)', border: 'none', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 14px rgba(29,190,116,0.35)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Neue Session starten
              </button>
            </div>
          ) : sessions.map(session => {
            const doneCount   = session.done?.length || 0;
            const total       = session.leadsSnapshot?.length || session.total || 0;
            const pct         = total > 0 ? Math.round((doneCount / total) * 100) : 0;
            const resumeLeads = (session.leadsSnapshot || []).map(l => allLeads.find(al => al.id === l.id) || l);
            const isRenaming  = editingId === session.id;
            const isAddOpen   = expandAdd === session.id;
            const existingIds = existingIdsForSession(session);

            const pickerLeads = allLeads.filter(l => {
              if (existingIds.has(l.id)) return false;
              if (!addSearch.trim()) return true;
              const q = addSearch.toLowerCase();
              return l.name.toLowerCase().includes(q) || l.company.toLowerCase().includes(q);
            });

            return (
              <div key={session.id} style={{ background: 'var(--bg3)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 12, overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(29,190,116,0.1)', border: '1.5px solid rgba(29,190,116,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#159c5f" strokeWidth="1.8">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.08 12.08 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.08 12.08 0 002.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isRenaming ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                        <input autoFocus value={editingName} onChange={e => setEditingName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveName(session.id); if (e.key === 'Escape') setEditingId(null); }}
                          style={{ flex: 1, height: 32, borderRadius: 7, border: '1.5px solid var(--accent)', padding: '0 10px', fontSize: 14, fontWeight: 600, background: 'var(--bg2)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }} />
                        <button onClick={() => saveName(session.id)} style={{ width: 30, height: 30, borderRadius: 7, background: 'var(--accent)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                        </button>
                        <button onClick={() => setEditingId(null)} style={{ width: 30, height: 30, borderRadius: 7, background: 'var(--bg4)', border: '1px solid var(--border)', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{session.name}</div>
                        <button onClick={() => { setEditingId(session.id); setEditingName(session.name); }} title="Umbenennen"
                          style={{ width: 22, height: 22, borderRadius: 5, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .1s', padding: 0 }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg4)'; e.currentTarget.style.color = 'var(--text)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text3)'; }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
                      Erstellt: {fmtSessionDate(session.createdAt)}
                      {session.updatedAt !== session.createdAt && <span> · Zuletzt: {fmtSessionDate(session.updatedAt)}</span>}
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>Position: Lead {Math.min((session.index || 0) + 1, total)}/{total}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a' }}>{doneCount}/{total} abgeschlossen</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--bg4)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg,#16a34a,#22c55e)', borderRadius: 3 }} />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {(session.leadsSnapshot || []).slice(0, 6).map((l, i) => (
                        <span key={l.id} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20,
                          background: session.done?.includes(l.id) ? 'rgba(22,163,74,0.1)' : i === (session.index || 0) ? 'rgba(29,190,116,0.1)' : 'var(--bg4)',
                          color: session.done?.includes(l.id) ? '#16a34a' : i === (session.index || 0) ? 'var(--accent)' : 'var(--text3)',
                          border: `1px solid ${session.done?.includes(l.id) ? 'rgba(22,163,74,0.25)' : i === (session.index || 0) ? 'rgba(29,190,116,0.25)' : 'var(--border)'}`,
                          fontWeight: i === (session.index || 0) ? 600 : 400,
                        }}>{l.name}</span>
                      ))}
                      {(session.leadsSnapshot?.length || 0) > 6 && (
                        <span style={{ fontSize: 11, color: 'var(--text3)', padding: '2px 6px' }}>+{session.leadsSnapshot.length - 6} weitere</span>
                      )}
                    </div>

                    <button onClick={() => openAddLeads(session.id)}
                      style={{ height: 28, padding: '0 11px', borderRadius: 7, border: `1.5px solid ${isAddOpen ? 'var(--accent-border)' : 'var(--border)'}`, background: isAddOpen ? 'var(--accent-bg)' : 'var(--bg4)', color: isAddOpen ? 'var(--accent)' : 'var(--text3)', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, transition: 'all .12s', fontFamily: 'inherit' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Leads hinzufügen
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => { onResume(session, resumeLeads); onClose(); }}
                      style={{ height: 32, padding: '0 14px', borderRadius: 7, background: 'linear-gradient(135deg,#0f8a53,#159c5f)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(29,190,116,0.3)' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      Fortsetzen
                    </button>
                    <button onClick={() => deleteSession(session.id)}
                      style={{ height: 32, padding: '0 12px', borderRadius: 7, background: 'var(--bg4)', border: '1px solid var(--border)', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', transition: 'all .12s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.3)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
                      Löschen
                    </button>
                  </div>
                </div>

                {isAddOpen && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px', background: 'var(--bg2)' }}>
                    <div style={{ position: 'relative', marginBottom: 10 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }}>
                        <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
                      </svg>
                      <input autoFocus type="text" placeholder="Lead oder Unternehmen suchen…" value={addSearch} onChange={e => setAddSearch(e.target.value)}
                        style={{ width: '100%', height: 34, borderRadius: 7, border: '1.5px solid var(--accent)', padding: '0 10px 0 32px', fontSize: 13, background: 'var(--bg3)', color: 'var(--text)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {pickerLeads.length === 0 ? (
                        <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>
                          {addSearch ? 'Keine Treffer' : 'Alle Leads sind bereits in dieser Session'}
                        </div>
                      ) : pickerLeads.map(l => {
                        const checked = addSelected.has(l.id);
                        return (
                          <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7, cursor: 'pointer', background: checked ? 'var(--accent-bg)' : 'transparent', border: `1px solid ${checked ? 'var(--accent-border)' : 'transparent'}`, transition: 'all .1s' }}>
                            <input type="checkbox" checked={checked}
                              onChange={() => setAddSelected(prev => { const n = new Set(prev); n.has(l.id) ? n.delete(l.id) : n.add(l.id); return n; })}
                              style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)', flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.company} · {l.city}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => confirmAddLeads(session)} disabled={addSelected.size === 0}
                        style={{ flex: 1, height: 34, borderRadius: 8, background: addSelected.size > 0 ? 'linear-gradient(135deg,#0f8a53,#159c5f)' : 'var(--bg4)', border: 'none', color: addSelected.size > 0 ? '#fff' : 'var(--text3)', fontSize: 13, fontWeight: 600, cursor: addSelected.size > 0 ? 'pointer' : 'default', transition: 'all .15s', fontFamily: 'inherit', boxShadow: addSelected.size > 0 ? '0 2px 8px rgba(29,190,116,0.3)' : 'none' }}>
                        {addSelected.size > 0 ? `${addSelected.size} Lead${addSelected.size > 1 ? 's' : ''} hinzufügen` : 'Leads auswählen'}
                      </button>
                      <button onClick={() => { setExpandAdd(null); setAddSelected(new Set()); }}
                        style={{ height: 34, padding: '0 16px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', gap: 10 }}>
          <button onClick={onNew}
            style={{ flex: 1, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#0d9488,#159c5f)', border: 'none', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, boxShadow: '0 2px 10px rgba(29,190,116,0.3)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Leads auswählen & neue Session starten
          </button>
          <button onClick={onClose}
            style={{ height: 40, padding: '0 18px', borderRadius: 10, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
